import { motion } from "framer-motion";
import { Calipers, VMC } from "@/components/site/DecorativeBG";

const ORANGE = "#F5A623";

const COMPLIANCE = [
  "ISO Compliant Processes",
  "International Quality Standards",
  "Material Traceability",
  "High Precision & Consistency",
  "On-Time Delivery Commitment",
  "Continuous Improvement",
];

const PACKAGING = [
  "Export Standard Packaging",
  "Custom Packaging Solutions",
  "Moisture Protection",
  "Labelling & Marking",
  "Global Shipping Support",
  "Door-to-Door Delivery",
];

/* ---------- Certified Quality badge ---------- */
function CertifiedBadge() {
  return (
    <svg viewBox="0 0 240 240" className="w-full h-full">
      <defs>
        <radialGradient id="medalGrad" cx="50%" cy="40%" r="60%">
          <stop offset="0%" stopColor="#F8C657" />
          <stop offset="60%" stopColor={ORANGE} />
          <stop offset="100%" stopColor="#B26A0A" />
        </radialGradient>
        <linearGradient id="ribbon" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={ORANGE} />
          <stop offset="100%" stopColor="#B26A0A" />
        </linearGradient>
      </defs>

      {/* ribbons */}
      <path d="M70 170 L70 230 L100 210 L120 230 L120 170 Z" fill="url(#ribbon)" />
      <path d="M170 170 L170 230 L140 210 L120 230 L120 170 Z" fill="#B26A0A" />

      {/* outer star burst */}
      <g transform="translate(120 110)">
        {Array.from({ length: 28 }).map((_, i) => {
          const a = (i / 28) * Math.PI * 2;
          const r1 = 85;
          const r2 = i % 2 === 0 ? 100 : 92;
          const x1 = Math.cos(a) * r1;
          const y1 = Math.sin(a) * r1;
          const x2 = Math.cos(a) * r2;
          const y2 = Math.sin(a) * r2;
          return (
            <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={ORANGE} strokeWidth="4" strokeLinecap="round" />
          );
        })}
      </g>

      {/* medal disc */}
      <circle cx="120" cy="110" r="80" fill="url(#medalGrad)" stroke="#7A1F2E" strokeWidth="2" />
      <circle cx="120" cy="110" r="64" fill="none" stroke="#FFFFFF" strokeOpacity="0.6" strokeWidth="1.4" />

      {/* curved top text */}
      <defs>
        <path id="topArc" d="M 56 110 A 64 64 0 0 1 184 110" fill="none" />
        <path id="botArc" d="M 56 110 A 64 64 0 0 0 184 110" fill="none" />
      </defs>
      <text fontSize="11" fontWeight="800" letterSpacing="4" fill="#FFFFFF" fontFamily="Manrope, sans-serif">
        <textPath href="#topArc" startOffset="50%" textAnchor="middle">CERTIFIED QUALITY</textPath>
      </text>
      <text fontSize="9" fontWeight="700" letterSpacing="3" fill="#FFFFFF" fontFamily="Manrope, sans-serif">
        <textPath href="#botArc" startOffset="50%" textAnchor="middle">100% GUARANTEE</textPath>
      </text>

      {/* big tick */}
      <path d="M90 112 L114 134 L154 90" fill="none" stroke="#FFFFFF" strokeWidth="9" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ---------- Logistics shipping graphic ---------- */
function LogisticsBadge() {
  return (
    <svg viewBox="0 0 280 200" className="w-full h-full">
      <defs>
        <linearGradient id="container" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1E3A5F" />
          <stop offset="100%" stopColor="#0B2E59" />
        </linearGradient>
        <linearGradient id="ground" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.0" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0.08" />
        </linearGradient>
      </defs>

      {/* ground */}
      <rect x="0" y="160" width="280" height="40" fill="url(#ground)" />
      <line x1="0" y1="160" x2="280" y2="160" stroke="#FFFFFF" strokeOpacity="0.18" strokeWidth="1" />

      {/* dashed route lines */}
      <path d="M0 168 L280 168" stroke="#FFFFFF" strokeOpacity="0.15" strokeWidth="1" strokeDasharray="6 8" />

      {/* container — main */}
      <g transform="translate(50 70)">
        <rect width="170" height="90" rx="2" fill="url(#container)" stroke={ORANGE} strokeWidth="1.5" />
        {/* corrugation lines */}
        {Array.from({ length: 14 }).map((_, i) => (
          <line key={i} x1={12 + i * 11} y1="10" x2={12 + i * 11} y2="80" stroke="#FFFFFF" strokeOpacity="0.15" strokeWidth="1" />
        ))}
        {/* door bars */}
        <rect x="138" y="14" width="6" height="62" fill="none" stroke="#FFFFFF" strokeOpacity="0.35" strokeWidth="1" />
        <rect x="156" y="14" width="6" height="62" fill="none" stroke="#FFFFFF" strokeOpacity="0.35" strokeWidth="1" />
        {/* TGSC badge on container */}
        <rect x="20" y="30" width="80" height="34" fill="#FFFFFF" fillOpacity="0.08" stroke={ORANGE} strokeWidth="1" />
        <text x="60" y="48" textAnchor="middle" fontSize="12" fontWeight="800" letterSpacing="3" fill={ORANGE} fontFamily="Manrope, sans-serif">TGSC</text>
        <text x="60" y="60" textAnchor="middle" fontSize="7" fontWeight="700" letterSpacing="2" fill="#FFFFFF" fillOpacity="0.85" fontFamily="Manrope, sans-serif">EXPORT GRADE</text>
        {/* corner brackets */}
        <rect x="0" y="0" width="6" height="6" fill={ORANGE} />
        <rect x="164" y="0" width="6" height="6" fill={ORANGE} />
        <rect x="0" y="84" width="6" height="6" fill={ORANGE} />
        <rect x="164" y="84" width="6" height="6" fill={ORANGE} />
      </g>

      {/* second smaller container peek */}
      <g transform="translate(220 100)" opacity="0.5">
        <rect width="50" height="60" rx="2" fill="url(#container)" stroke={ORANGE} strokeWidth="1" />
        {Array.from({ length: 4 }).map((_, i) => (
          <line key={i} x1={6 + i * 11} y1="6" x2={6 + i * 11} y2="54" stroke="#FFFFFF" strokeOpacity="0.15" strokeWidth="1" />
        ))}
      </g>

      {/* moving arrow */}
      <g transform="translate(140 20)">
        <path d="M -60 16 L 60 16" stroke={ORANGE} strokeWidth="2" strokeDasharray="4 6" />
        <path d="M 50 8 L 64 16 L 50 24" fill="none" stroke={ORANGE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <text x="0" y="10" textAnchor="middle" fontSize="9" fontWeight="800" letterSpacing="3" fill="#FFFFFF" fillOpacity="0.8" fontFamily="Manrope, sans-serif">EU · UK · USA · MEA</text>
      </g>

      {/* globe miniature in corner */}
      <g transform="translate(38 38)" opacity="0.85">
        <circle r="16" fill="none" stroke={ORANGE} strokeWidth="1.4" />
        <ellipse rx="16" ry="6" fill="none" stroke={ORANGE} strokeWidth="1" />
        <ellipse rx="6" ry="16" fill="none" stroke={ORANGE} strokeWidth="1" />
        <line x1="-16" y1="0" x2="16" y2="0" stroke={ORANGE} strokeWidth="1" />
      </g>
    </svg>
  );
}

export default function LogisticsCompliance() {
  return (
    <section
      id="logistics"
      data-testid="logistics-section"
      className="relative py-24 lg:py-32 bg-brand-navy text-white overflow-hidden"
    >
      <div className="absolute inset-0 bp-grid opacity-25 pointer-events-none" />
      <div className="absolute -top-32 right-0 h-80 w-80 rounded-full bg-brand-earth/15 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 left-0 h-80 w-80 rounded-full bg-brand-orange/10 blur-3xl pointer-events-none" />
      <VMC className="top-24 right-2 lg:right-10 hidden lg:block" opacity={0.22} tone="white" bounce />
      <Calipers className="bottom-24 left-2 lg:left-6 hidden lg:block" opacity={0.18} bounce />

      <div className="container-x relative">
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-5">
              <span className="divider-rule" />
              <span className="eyebrow !text-white/80">Logistics, Quality &amp; Compliance Standard</span>
            </div>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-[1.05]">
              One accountable bridge —
              <br />
              <span className="text-brand-orange">from quality lab to your dock.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 text-white/75 leading-relaxed">
            Every batch is verified in our in-house quality lab, packaged to
            export standards and shipped under a single consolidated
            consignment — fully documented, fully traceable, fully on time.
          </div>
        </div>

        {/* Quality & Compliance block — badge graphic + bullets */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 flex justify-center"
            data-testid="certified-quality-badge"
          >
            <div className="w-[280px] h-[280px] sm:w-[320px] sm:h-[320px]">
              <CertifiedBadge />
            </div>
          </motion.div>
          <div className="lg:col-span-7">
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-orange font-bold mb-3">
              Quality &amp; Compliance Standard
            </div>
            <h3 className="font-heading font-extrabold text-2xl lg:text-3xl text-white leading-tight">
              The way Tirupati Global Sourcing Company works.
            </h3>
            <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5">
              {COMPLIANCE.map((c) => (
                <div
                  key={c}
                  data-testid={`compliance-${c.split(" ")[0].toLowerCase()}`}
                  className="flex items-center gap-3 text-white/90 text-sm py-2 border-b border-white/10"
                >
                  <span className="h-1.5 w-1.5 bg-brand-orange shrink-0" />
                  <span className="font-semibold">{c}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* divider */}
        <div className="my-16 h-px bg-white/10" />

        {/* Packaging & Logistic block — logistics graphic + bullets */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-orange font-bold mb-3">
              Packaging &amp; Logistic Support
            </div>
            <h3 className="font-heading font-extrabold text-2xl lg:text-3xl text-white leading-tight">
              Export-grade. End to end.
            </h3>
            <p className="mt-4 text-white/70 leading-relaxed text-sm">
              VCI, heat-treated crates and bespoke packaging engineered for
              ocean and air freight — coordinated with consolidated shipping
              and customs documentation.
            </p>
            <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5">
              {PACKAGING.map((c) => (
                <div
                  key={c}
                  data-testid={`packaging-${c.split(" ")[0].toLowerCase()}`}
                  className="flex items-center gap-3 text-white/90 text-sm py-2 border-b border-white/10"
                >
                  <span className="h-1.5 w-1.5 bg-brand-orange shrink-0" />
                  <span className="font-semibold">{c}</span>
                </div>
              ))}
            </div>
          </div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 order-1 lg:order-2 flex justify-center"
            data-testid="logistics-graphic"
          >
            <div className="w-full max-w-[420px]">
              <LogisticsBadge />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
